import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Zap } from "lucide-react";
import { Link } from "wouter";
import { useLang } from "@/i18n/LangContext";

const DARK_GRAD = "linear-gradient(150deg, #0d2137 0%, #0a0f1e 55%, #060c18 100%)";

export function SocialPricingSection() {
  const { tr } = useLang();
  const sp = tr.socialPricing;

  return (
    <section
      className="py-32 px-6 text-foreground relative overflow-hidden"
      style={{ background: DARK_GRAD }}
    >
      <div className="absolute inset-0 dark-texture pointer-events-none" />
      <div className="max-w-7xl mx-auto relative z-10">

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-6"
        >
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary mb-4 px-3 py-1.5 bg-primary/10 rounded-full">
            <Zap className="w-3.5 h-3.5" /> {sp.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-4">
            {sp.title}
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {sp.desc}
          </p>
        </motion.div>

        {/* Launch offer banner */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex items-center justify-center gap-3 mb-14"
        >
          <div className="flex items-center gap-2 bg-amber-500/15 border border-amber-500/30 text-amber-300 text-sm font-semibold px-4 py-2 rounded-full">
            <Zap className="w-4 h-4 text-amber-400" />
            {sp.launchBanner}
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 items-start">
          {sp.plans.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`rounded-3xl flex flex-col relative ${
                plan.highlight
                  ? "bg-white text-slate-900 shadow-[0_0_40px_rgba(56,189,248,0.2)] border-2 border-primary md:-translate-y-4"
                  : "bg-white/5 border border-primary/20 text-foreground hover:border-primary/40 transition-colors"
              }`}
            >
              {plan.highlight && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-white text-xs font-bold uppercase tracking-widest py-1.5 px-4 rounded-full">
                  {sp.mostPopular}
                </div>
              )}

              <div className="p-8">
                <h3 className={`text-xl font-bold mb-2 ${plan.highlight ? "text-slate-900" : "text-white"}`}>
                  {plan.name}
                </h3>
                <p className={`text-sm min-h-[52px] mb-6 leading-relaxed ${plan.highlight ? "text-slate-500" : "text-muted-foreground"}`}>
                  {plan.tagline}
                </p>

                {/* Pricing */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-4xl font-extrabold text-primary">
                      RD$ {plan.launchPrice}
                    </span>
                    <span className={`text-sm font-medium ${plan.highlight ? "text-slate-500" : "text-muted-foreground"}`}>
                      {sp.month}
                    </span>
                  </div>
                  <p className={`text-xs flex items-center gap-1 ${plan.highlight ? "text-slate-400" : "text-muted-foreground"}`}>
                    <span>{sp.regularPrice}</span>
                    <span className="line-through">RD$ {plan.regularPrice}</span>
                  </p>
                </div>

                <ul className="space-y-2.5">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5">
                      <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-primary" />
                      <span className={`text-sm ${plan.highlight ? "text-slate-700" : "text-slate-300"}`}>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="px-8 pb-8 mt-auto">
                <Link
                  href="/contacto"
                  className={`inline-flex items-center justify-center rounded-xl text-sm font-bold h-12 w-full transition-all ${
                    plan.highlight
                      ? "bg-primary text-white hover:bg-primary/90 shadow-[0_0_20px_rgba(56,189,248,0.3)]"
                      : "bg-white/10 text-white hover:bg-white/20 border border-white/20"
                  }`}
                >
                  {sp.cta}
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-muted-foreground text-sm mt-12"
        >
          {sp.bottomText}{" "}
          <Link href="/contacto" className="text-primary font-semibold hover:underline">
            {sp.bottomLink}
          </Link>
        </motion.p>
      </div>
    </section>
  );
}
